'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Square, Volume2, SkipForward, SkipBack } from 'lucide-react';

interface TtsPlayerProps {
  content: string; // Raw markdown content of the lesson
}

function cleanMarkdownForSpeech(markdown: string): string {
  if (!markdown) return '';
  let cleaned = markdown;
  
  // 1. Remove YAML frontmatter (at the start of the file)
  cleaned = cleaned.replace(/^---[\s\S]*?---\r?\n/, '');
  
  // 2. Remove multi-line code blocks
  cleaned = cleaned.replace(/```[\s\S]*?```/g, '');
  
  // 3. Remove custom self-closing JSX elements (e.g. <MultipleChoiceMechanic ... />)
  cleaned = cleaned.replace(/<[A-Z][a-zA-Z0-9]+[^>]*\/>/g, '');
  
  // 4. Remove paired JSX tags and their content if they are likely components (e.g., <EvidenceMatchMechanic> ... </EvidenceMatchMechanic>)
  cleaned = cleaned.replace(/<([A-Z][a-zA-Z0-9]+)[\s\S]*?<\/\1>/g, '');
  
  // 5. Remove any other HTML/XML-like tags but keep content (e.g., <p>text</p> -> text)
  cleaned = cleaned.replace(/<[^>]+>/g, '');
  
  // 6. Remove Markdown images: ![alt](url)
  cleaned = cleaned.replace(/!\[[^\]]*\]\([^)]+\)/g, '');
  
  // 7. Extract link text from Markdown links: [Link text](url) -> Link text
  cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  
  // 8. Remove inline code backticks: `code` -> code
  cleaned = cleaned.replace(/`([^`]+)`/g, '$1');
  
  // 9. Strip bold, italic, strikethrough markers: **, *, _, ~~
  cleaned = cleaned.replace(/(\*\*|__)(.*?)\1/g, '$2');
  cleaned = cleaned.replace(/(\*|_)(.*?)\1/g, '$2');
  cleaned = cleaned.replace(/(~~)(.*?)\1/g, '$2');
  
  // 10. Clean headers markers (e.g., ### Heading -> Heading)
  cleaned = cleaned.replace(/^#{1,6}\s+(.*)$/gm, '$1');
  
  // 11. Clean blockquote markers
  cleaned = cleaned.replace(/^>\s+(.*)$/gm, '$1');
  
  // 12. Clean list indicators (remove bullet points, numbers, but keep text)
  cleaned = cleaned.replace(/^[-*+]\s+/gm, '');
  cleaned = cleaned.replace(/^\d+\.\s+/gm, '');
  
  // 13. Remove excessive empty lines and double whitespaces
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  cleaned = cleaned.replace(/[ \t]+/g, ' ');
  
  return cleaned.trim();
}

// Split text into short, readable chunks (sentences) to avoid Web Speech API cut-off issues
function splitIntoSentences(text: string): string[] {
  if (!text) return [];
  // Split by sentence-ending punctuation followed by space or newline
  const rawChunks = text.split(/(?<=[.!?])\s+|\n+/);
  return rawChunks
    .map(c => c.trim())
    .filter(c => c.length > 0);
}

export default function TtsPlayer({ content }: TtsPlayerProps) {
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoiceName, setSelectedVoiceName] = useState<string>('');
  const [rate, setRate] = useState<number>(1.0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [sentences, setSentences] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isSupported, setIsSupported] = useState<boolean>(true);
  
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  // Filter voices: Google US English and Microsoft US English
  const displayedVoices = React.useMemo(() => {
    const filtered = voices.filter(v => {
      const nameLower = v.name.toLowerCase();
      const langLower = v.lang.toLowerCase();
      
      // Google US English
      if (nameLower.includes('google us english')) return true;
      
      // Microsoft US English
      if (nameLower.includes('microsoft') && (langLower.includes('en-us') || nameLower.includes('united states'))) return true;
      
      return false;
    });

    return filtered.length > 0 ? filtered : voices.filter(v => v.lang.toLowerCase().startsWith('en'));
  }, [voices]);

  // Clean voice name for professional display in dropdown
  const formatVoiceName = (name: string): string => {
    if (name.toLowerCase().includes('google us english')) {
      return 'Amy (Google)';
    }
    
    // Format Microsoft voices to just display "Name (Microsoft)" or "Name (Microsoft Natural)"
    if (name.toLowerCase().includes('microsoft')) {
      const match = name.match(/Microsoft\s+([A-Za-z]+)/i);
      if (match) {
        const voiceName = match[1];
        const isNatural = name.toLowerCase().includes('natural') || name.toLowerCase().includes('online');
        return `${voiceName} (Microsoft${isNatural ? ' Natural' : ''})`;
      }
      return name.replace(/\s*-\s*English.*/i, '').trim();
    }
    
    return name;
  };

  // Initialize and load voices
  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      setIsSupported(false);
      return;
    }

    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices();
      setVoices(availableVoices);
      
      // Auto-select a nice voice
      if (availableVoices.length > 0) {
        const filtered = availableVoices.filter(v => {
          const nameLower = v.name.toLowerCase();
          const langLower = v.lang.toLowerCase();
          if (nameLower.includes('google us english')) return true;
          if (nameLower.includes('microsoft') && (langLower.includes('en-us') || nameLower.includes('united states'))) return true;
          return false;
        });

        const displayed = filtered.length > 0 ? filtered : availableVoices.filter(v => v.lang.toLowerCase().startsWith('en'));
        const defaultVoice = displayed.find(v => v.default) || displayed[0] || availableVoices[0];
        setSelectedVoiceName(defaultVoice.name);
      }
    };

    loadVoices();
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Prepare sentences when content changes
  useEffect(() => {
    const cleaned = cleanMarkdownForSpeech(content);
    const split = splitIntoSentences(cleaned);
    setSentences(split);
    setCurrentIndex(0);
    stopSpeech();
  }, [content]);

  const speakCurrentSentence = (index: number) => {
    if (!window.speechSynthesis || sentences.length === 0 || index >= sentences.length) {
      setIsPlaying(false);
      setIsPaused(false);
      return;
    }

    window.speechSynthesis.cancel(); // Cancel any current speech

    const textToSpeak = sentences[index];
    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    utteranceRef.current = utterance;

    // Apply voice settings
    const selectedVoice = voices.find(v => v.name === selectedVoiceName);
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }
    utterance.rate = rate;

    // Callback when current sentence ends
    utterance.onend = () => {
      if (index + 1 < sentences.length) {
        setCurrentIndex(index + 1);
        speakCurrentSentence(index + 1);
      } else {
        setIsPlaying(false);
        setIsPaused(false);
        setCurrentIndex(0);
      }
    };

    utterance.onerror = (e) => {
      if (e.error !== 'interrupted') {
        console.error('SpeechSynthesis error:', e);
        setIsPlaying(false);
        setIsPaused(false);
      }
    };

    window.speechSynthesis.speak(utterance);
  };

  const startSpeech = () => {
    if (sentences.length === 0) return;
    setIsPlaying(true);
    setIsPaused(false);
    speakCurrentSentence(currentIndex);
  };

  const pauseSpeech = () => {
    if (!window.speechSynthesis) return;
    if (isPlaying && !isPaused) {
      window.speechSynthesis.pause();
      setIsPaused(true);
    } else if (isPlaying && isPaused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
    }
  };

  const stopSpeech = () => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentIndex(0);
  };

  const handleNext = () => {
    if (currentIndex + 1 < sentences.length) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      if (isPlaying) {
        speakCurrentSentence(nextIndex);
      }
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      const prevIndex = currentIndex - 1;
      setCurrentIndex(prevIndex);
      if (isPlaying) {
        speakCurrentSentence(prevIndex);
      }
    }
  };

  if (!isSupported) {
    return null; // Don't render if browser doesn't support Web Speech API
  }

  return (
    <div className="flex flex-row flex-wrap sm:flex-nowrap items-center justify-between gap-3 px-3 py-2 bg-muted/40 dark:bg-zinc-900/50 border border-border/80 rounded-xl my-4 text-xs">
      
      {/* 1. Status Info & Mini Icon */}
      <div className="flex items-center gap-2 shrink-0">
        <Volume2 className={`w-4 h-4 text-primary ${isPlaying && !isPaused ? 'animate-pulse' : ''}`} />
        <span className="font-semibold text-[10px] text-muted-foreground uppercase tracking-wider hidden xs:inline">
          {isPlaying 
            ? `${currentIndex + 1}/${sentences.length}` 
            : 'Read Aloud'}
        </span>
      </div>

      {/* 2. Playback Control Buttons */}
      <div className="flex items-center gap-1">
        <button
          onClick={handlePrev}
          disabled={currentIndex === 0}
          className="p-1.5 rounded-lg hover:bg-foreground/5 dark:hover:bg-white/5 disabled:opacity-40 transition-colors"
          title="Previous"
        >
          <SkipBack className="w-3.5 h-3.5" />
        </button>

        {!isPlaying ? (
          <button
            onClick={startSpeech}
            disabled={sentences.length === 0}
            className="flex items-center justify-center p-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/95 transition-all shadow-sm active:scale-95 disabled:opacity-40"
            title="Play"
          >
            <Play className="w-3.5 h-3.5 fill-current" />
          </button>
        ) : (
          <div className="flex items-center gap-1">
            <button
              onClick={pauseSpeech}
              className="flex items-center justify-center p-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-all shadow-sm active:scale-95"
              title={isPaused ? 'Resume' : 'Pause'}
            >
              {isPaused ? <Play className="w-3.5 h-3.5 fill-current" /> : <Pause className="w-3.5 h-3.5 fill-current" />}
            </button>
            <button
              onClick={stopSpeech}
              className="flex items-center justify-center p-1.5 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/95 transition-all shadow-sm active:scale-95"
              title="Stop"
            >
              <Square className="w-3.5 h-3.5 fill-current" />
            </button>
          </div>
        )}

        <button
          onClick={handleNext}
          disabled={currentIndex >= sentences.length - 1}
          className="p-1.5 rounded-lg hover:bg-foreground/5 dark:hover:bg-white/5 disabled:opacity-40 transition-colors"
          title="Next"
        >
          <SkipForward className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* 3. Voice Dropdown */}
      <div className="flex-1 min-w-[120px] max-w-[180px]">
        <select
          value={selectedVoiceName}
          onChange={(e) => {
            setSelectedVoiceName(e.target.value);
            if (isPlaying) {
              setTimeout(() => speakCurrentSentence(currentIndex), 50);
            }
          }}
          className="w-full bg-background border border-border rounded-lg px-2 py-1 outline-none text-xs cursor-pointer focus:ring-1 focus:ring-primary"
        >
          {displayedVoices.map((v) => (
            <option key={v.name} value={v.name} className="bg-background text-foreground text-xs">
              {formatVoiceName(v.name)}
            </option>
          ))}
        </select>
      </div>

      {/* 4. Speed Rate Slider */}
      <div className="flex items-center gap-2 shrink-0 border-l border-border/80 pl-2">
        <span className="text-[10px] text-muted-foreground font-mono font-semibold">{rate}x</span>
        <input
          type="range"
          min="0.5"
          max="2.0"
          step="0.1"
          value={rate}
          onChange={(e) => {
            const val = parseFloat(e.target.value);
            setRate(val);
            if (isPlaying) {
              setTimeout(() => speakCurrentSentence(currentIndex), 50);
            }
          }}
          className="w-14 accent-primary cursor-pointer bg-primary/20 h-1 rounded-lg outline-none"
        />
      </div>

    </div>
  );
}
